import { Link } from "react-router-dom";
import {
  TrendingUp,
  Shield,
  PoundSterling,
  Wallet,
  ArrowRight,
  Calculator,
  Clock,
  PieChart,
  Flame,
} from "lucide-react";

const TOOLS = [
  {
    icon: PieChart,
    title: "Comprehensive Budget Calculator",
    shortTitle: "Budget Calculator",
    desc: "Track your income, expenses, savings, and debts all in one place. See how your budget compares to the 50/30/20 rule with a Budget Health Score and Excel export.",
    href: "/tools/budget-calculator",
    tag: "Budgeting",
    tagColor: "violet",
    image: "https://images.pexels.com/photos/4386431/pexels-photo-4386431.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-budget-calculator",
    features: ["Itemised expenses", "Budget Health Score", "Excel export"],
  },
  {
    icon: Flame,
    title: "FIRE Calculator UK",
    shortTitle: "FIRE Calculator",
    desc: "Calculate your path to Financial Independence, Retire Early. Based on the 25× rule with adjustable withdrawal rates and a visual timeline to your FIRE date.",
    href: "/tools/fire-calculator",
    tag: "Retirement",
    tagColor: "orange",
    image: "https://images.pexels.com/photos/3943716/pexels-photo-3943716.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-fire-calculator",
    features: ["25× rule", "Withdrawal rate options", "FIRE timeline chart"],
  },
  {
    icon: TrendingUp,
    title: "Compound Interest Calculator",
    shortTitle: "Compound Interest",
    desc: "Project your investments over time. See how your money grows with different rates, contribution amounts, and time horizons. The UK investor's most powerful planning tool.",
    href: "/tools/compound-interest",
    tag: "Investing",
    tagColor: "emerald",
    image: "https://images.pexels.com/photos/6203470/pexels-photo-6203470.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-compound-interest",
    features: ["Annual & monthly compounding", "Regular contributions", "Inflation adjustment"],
  },
  {
    icon: PoundSterling,
    title: "UK Take Home Pay",
    shortTitle: "Salary Calculator",
    desc: "Enter your gross salary and see your exact net pay after Income Tax, National Insurance, and pension contributions. Updated for 2024/25 tax year.",
    href: "/tools/salary-calculator",
    tag: "Salary",
    tagColor: "sky",
    image: "https://images.pexels.com/photos/5277965/pexels-photo-5277965.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-salary",
    features: ["2024/25 tax bands", "National Insurance", "Pension deductions"],
  },
  {
    icon: Shield,
    title: "ISA vs GIA Comparison",
    shortTitle: "ISA Calculator",
    desc: "Compare the long-term impact of investing inside a Stocks & Shares ISA versus a General Investment Account. Visualise exactly how much tax you save.",
    href: "/tools/isa-calculator",
    tag: "Tax Efficiency",
    tagColor: "violet",
    image: "https://images.pexels.com/photos/8665284/pexels-photo-8665284.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-isa",
    features: ["ISA allowance tracking", "Dividend tax comparison", "CGT modelling"],
  },
  {
    icon: Wallet,
    title: "Emergency Fund Calculator",
    shortTitle: "Emergency Fund",
    desc: "Calculate exactly how much you need in your financial safety net. Based on your monthly expenses and personal risk tolerance — in British pounds.",
    href: "/tools/emergency-fund",
    tag: "Planning",
    tagColor: "amber",
    image: "https://images.unsplash.com/photo-1696013910376-c56f76dd8178?crop=entropy&cs=srgb&fm=jpg&w=800",
    testId: "tool-card-emergency-fund",
    features: ["3-6 month rule", "Expense breakdown", "Savings timeline"],
  },
  {
    icon: Clock,
    title: "Cost of Waiting",
    shortTitle: "Cost of Delay",
    desc: "See the real financial cost of delaying your investments by 1–10 years. A visualised wake-up call with a live area chart showing the compounding gap.",
    href: "/tools/cost-of-waiting",
    tag: "Wake-Up Call",
    tagColor: "red",
    image: "https://images.pexels.com/photos/8665284/pexels-photo-8665284.jpeg?auto=compress&cs=tinysrgb&w=800",
    testId: "tool-card-cost-of-waiting",
    features: ["Delay comparison", "Recharts area chart", "Cost per month delayed"],
  },
];

const TAG_COLORS = {
  emerald: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20",
  sky: "bg-sky-500/10 text-sky-400 border-sky-500/20",
  violet: "bg-violet-500/10 text-violet-400 border-violet-500/20",
  amber: "bg-amber-500/10 text-amber-400 border-amber-500/20",
  red: "bg-red-500/10 text-red-400 border-red-500/20",
  orange: "bg-orange-500/10 text-orange-400 border-orange-500/20",
};

const ICON_COLORS = {
  emerald: "bg-emerald-500/10 text-emerald-400",
  sky: "bg-sky-500/10 text-sky-400",
  violet: "bg-violet-500/10 text-violet-400",
  amber: "bg-amber-500/10 text-amber-400",
  red: "bg-red-500/10 text-red-400",
  orange: "bg-orange-500/10 text-orange-400",
};

const HOVER_BORDERS = {
  emerald: "hover:border-emerald-500/40",
  sky: "hover:border-sky-500/40",
  violet: "hover:border-violet-500/40",
  amber: "hover:border-amber-500/40",
  red: "hover:border-red-500/40",
  orange: "hover:border-orange-500/40",
};

export default function Tools() {
  return (
    <div className="bg-slate-950 min-h-screen pt-16">
      {/* Header */}
      <div
        className="relative py-16 md:py-24 border-b border-slate-800/50"
        style={{
          background: "radial-gradient(ellipse 60% 60% at 30% 0%, rgba(16, 185, 129, 0.1), transparent)",
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-7 h-7 rounded-lg bg-emerald-500/20 flex items-center justify-center">
              <Calculator className="w-3.5 h-3.5 text-emerald-400" />
            </div>
            <span className="text-emerald-400 text-sm font-medium">All Tools</span>
          </div>
          <h1
            data-testid="tools-page-title"
            className="font-outfit text-4xl sm:text-5xl font-bold text-white mb-4 tracking-tight"
          >
            Tools & Calculators
          </h1>
          <p className="text-slate-400 text-lg max-w-2xl">
            Free, accurate UK-specific financial calculators. Built for British investors using HMRC
            rates, ISA allowances, and real NI thresholds.
          </p>
        </div>
      </div>

      {/* Tools Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {TOOLS.map((tool) => {
            const Icon = tool.icon;
            const tagCls = TAG_COLORS[tool.tagColor];
            const iconCls = ICON_COLORS[tool.tagColor];
            const hoverCls = HOVER_BORDERS[tool.tagColor];
            return (
              <Link
                key={tool.href}
                to={tool.href}
                data-testid={tool.testId}
                className={`group bg-slate-900/40 border border-slate-800 ${hoverCls} rounded-2xl overflow-hidden transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:shadow-black/30`}
              >
                {/* Image */}
                <div className="relative h-44 overflow-hidden">
                  <img
                    src={tool.image}
                    alt={tool.title}
                    className="w-full h-full object-cover opacity-40 group-hover:opacity-50 group-hover:scale-105 transition-all duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-transparent to-slate-900/90" />
                  <div className="absolute bottom-4 left-4">
                    <span className={`inline-flex text-xs font-medium border rounded-full px-2.5 py-0.5 ${tagCls}`}>
                      {tool.tag}
                    </span>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  <div className="flex items-start gap-4 mb-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${iconCls}`}>
                      <Icon className="w-5 h-5" />
                    </div>
                    <div>
                      <h2 className="font-outfit font-semibold text-white text-xl mb-1">{tool.title}</h2>
                      <p className="text-slate-400 text-sm leading-relaxed">{tool.desc}</p>
                    </div>
                  </div>

                  {/* Features */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {tool.features.map((f) => (
                      <span
                        key={f}
                        className="text-xs text-slate-500 bg-slate-800/60 border border-slate-700/50 rounded-full px-3 py-1"
                      >
                        {f}
                      </span>
                    ))}
                  </div>

                  <div className={`flex items-center gap-1.5 text-sm font-medium ${iconCls.split(" ")[1]}`}>
                    Open Calculator
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1.5 transition-transform duration-200" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
